import { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import ModalStackContext from './ModalStackContext';
import ModalStack from './ModalStack';

/**
 * Modals that open one on top of another in the same window, like the drawers of
 * `DrawerStackProvider`: each modal is a page, with a back button to the previous one.
 *
 * Modals are registered by ID, globally with `sharedModals` or by the component that opens them
 * with `useModalStackRegister`, and opened with `useModalStack().open(id, props)`. Each modal gets
 * its props plus `onBack` and `onClose`; `props.title` is the title of the window, and
 * `Component.modalStackProps` its options (`size`).
 */
export default function ModalStackProvider({ children, sharedModals = [] }) {
    const [modals, setModals] = useState([]);
    const [modalTypes, setModalTypes] = useState(() => Object.fromEntries(sharedModals));
    const [direction, setDirection] = useState(1);

    const register = useCallback((idOrModals, component) => {
        const newTypes = Array.isArray(idOrModals) ? Object.fromEntries(idOrModals) : { [idOrModals]: component };

        setModalTypes((prev) => ({ ...prev, ...newTypes }));

        return () => {
            setModalTypes((prev) => {
                const types = { ...prev };
                Object.keys(newTypes).forEach((id) => delete types[id]);
                return types;
            });
        };
    }, []);

    // The component is looked up when rendering, so a modal can be opened as its type registers.
    const open = useCallback((id, props = {}) => {
        setDirection(1);
        setModals((prev) => [...prev, { id, props }]);
    }, []);

    const back = useCallback(() => {
        modals.at(-1)?.props.onClose?.();

        setDirection(-1);
        setModals((prev) => prev.slice(0, -1));
    }, [modals]);

    const close = useCallback(() => {
        [...modals].reverse().forEach((modal) => modal.props.onClose?.());

        setModals([]);
    }, [modals]);

    const contextValue = useMemo(() => ({ register, open, back, close }), [register, open, back, close]);

    return (
        <ModalStackContext.Provider value={contextValue}>
            {children}
            <ModalStack modals={modals} modalTypes={modalTypes} direction={direction} onBack={back} onClose={close} />
        </ModalStackContext.Provider>
    );
}

ModalStackProvider.propTypes = {
    children: PropTypes.node.isRequired,
    sharedModals: PropTypes.arrayOf(PropTypes.array),
};
